import { useMemo } from 'react'

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  defaultDataIdFromObject,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import * as Sentry from '@sentry/nextjs'
import { GraphQLFormattedError } from 'graphql'
import { v4 as uuidv4 } from 'uuid'

import { LoadingSkeleton } from '../layout-components/CorePage'

import config from '@/config/index'
import { useAuth } from '@/context/authContext'
import {
  createContextLink,
  httpLink,
  manifestStyleSortTransform,
  persistedQueryLink,
} from '@/utils/customApolloLinks'

import { Register } from 'types/api.graphql'

const cache = new InMemoryCache({
  dataIdFromObject(responseObject) {
    switch (responseObject.__typename) {
      case 'Register' as Register['__typename']:
        return (
          'Register:' + responseObject.id + (responseObject.itemCount ?? '0')
        ) // required for the cached item count in the navigation menu when switching projects
      default:
        return defaultDataIdFromObject(responseObject)
    }
  },
})

const isMalformed = (errors: ReadonlyArray<GraphQLFormattedError>) => {
  return errors?.some((error) =>
    error.extensions?.originalError?.['message']
      ?.toLowerCase()
      .includes('jwt malformed')
  )
}

const isClerkJwtExpired = (
  errors: ReadonlyArray<GraphQLFormattedError>
): boolean => {
  return errors.some(
    (error) =>
      (
        error.extensions?.originalError as { message: string; stack: string }
      )?.message?.includes('JWT is expired') ||
      (
        error.extensions?.originalError as { message: string; stack: string }
      )?.message?.includes('TokenVerificationError')
  )
}

type Props = {
  extraApolloLinks?: ApolloLink[]
  children?: React.ReactNode
  loader?: React.ReactNode
}

export const AuthorisedApolloProvider: React.FC<Props> = ({
  extraApolloLinks,
  children,
  loader,
}) => {
  const { token } = useAuth()

  const client = useMemo(() => {
    if (!token) {
      return null
    }

    const authLink = new ApolloLink((operation, forward) => {
      const requestId = uuidv4()

      operation.setContext(({ headers }: { headers: Headers }) => ({
        headers: {
          ...headers,
          'auth-provider': 'clerk',
          authorization: `Bearer ${token}`,
          'x-buildpass-request-id': requestId,
        },
      }))

      return forward(operation)
    })

    /**
     * This link will check for the 'jwt expired' error and attempt to refresh the token and re-try the request.
     * If multiple requests fail, it will only attempt to refresh the token once and queue up the other requests to be re-tried once the token exchange is complete.
     * The code is ugly but this is how the internet told us to do it: https://stackoverflow.com/a/63386965
     */
    // Note: forward(operation) basically means retry the request
    const refreshTokenLink = onError(
      ({ graphQLErrors, networkError, forward, operation }) => {
        if (graphQLErrors?.length) {
          if (isClerkJwtExpired(graphQLErrors)) {
            // If JWT is expired client side, reload the page to refresh the token server side
            location.reload()
            return
          } else if (isMalformed(graphQLErrors)) {
            location.href = '/logout'
            return
          } else {
            // if query, forward the request
            if (operation.query) {
              return forward(operation)
            }

            // Do not retry
            Sentry.captureException(
              graphQLErrors.map((e) => e.message).join('\n'),
              {
                extra: {
                  operation,
                },
              }
            )
            return null
          }
        }

        if (networkError) {
          // Retry
          Sentry.captureException(networkError, {
            extra: {
              operation,
            },
          })
          console.error(`[Network error]: ${networkError}`)
          forward(operation)
        }

        Sentry.captureException(
          'Server error, not network or auth or graphql',
          {
            extra: {
              operation,
            },
          }
        )
        // Otherwise its probably a server error, so don't retry
        return null
      }
    )

    return new ApolloClient({
      link: ApolloLink.from([
        refreshTokenLink,
        ...(extraApolloLinks ?? []),
        authLink,
        createContextLink(),
        persistedQueryLink,
        httpLink,
      ]),
      cache,
      name: `${config?.name}-authorised`,
      version: config?.version,
      queryDeduplication: false,
      documentTransform: manifestStyleSortTransform,
    })
  }, [extraApolloLinks, token])

  if (!client) {
    return loader ?? <LoadingSkeleton />
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}
